/**
 * onboardingFlow - the first-run comfort question and its side effects.
 *
 * The question ("How would you like to explore?") renders INLINE on the boot
 * screen while the world loads (src/ui/bootScreen.js) - there is no post-load
 * modal anymore, so the first look at the 3D space is never blocked. Cards:
 *   #onboard-scheme-classic - "I play games"          -> classic
 *   #onboard-scheme-hybrid  - "Keys, free mouse"     -> hybrid
 *   #onboard-scheme-easy    - "Keep it simple"        -> easy
 *   #onboard-mode-follow    - "Follow the guided tour"-> follow mode + easy
 * Touch devices get a single "Start exploring" card plus the follow card.
 *
 * A card click persists the choice (onboardingStore) and fires analytics;
 * the scheme/movement side effects are applied at reveal time by
 * arrivalSequence.js (the HUD and scene exist by then), which also starts
 * the matching in-world coach-mark sequence (coachMarks.js). Coach marks
 * auto-dismiss as the user performs each action and are replayable from the
 * Help panel.
 *
 * Persistence: localStorage `oggi_onboarding_v1` via onboardingStore.js -
 * deliberately NOT in settingsStore, so "Reset all settings" does not replay
 * onboarding. Returning users with no stored scheme keep the classic default
 * (preserves the historical behavior).
 */

import { createIcon, BRAND } from '../designSystem.js';
import { setScheme, getScheme, isTouchDevice } from '../../input/controlScheme.js';
import { setMovementMode } from '../../movementMode.js';
import { showMovementModeToggle } from '../multiplayerUI.js';
import { startCoachMarks } from './coachMarks.js';
import { analytics } from '../../services/analytics.js';
import { Gamepad2, MousePointerClick, Footprints, Compass, Hand } from 'lucide';
import { markCompleted } from './onboardingStore.js';

export { hasCompletedOnboarding } from './onboardingStore.js';

/** The card definitions, keyed by the persisted/analytics choice value. */
const CARDS = {
  classic: {
    id: 'onboard-scheme-classic',
    icon: Gamepad2,
    title: 'I play games',
    body: 'Classic controls: walk with W A S D, look with the mouse - like any first-person game.',
  },
  hybrid: {
    id: 'onboard-scheme-hybrid',
    icon: Hand,
    title: 'Keys, free mouse',
    body: 'Walk with W A S D and drag to look. Your cursor is never captured.',
  },
  easy: {
    id: 'onboard-scheme-easy',
    icon: MousePointerClick,
    title: 'Keep it simple',
    body: 'Click where you want to go and drag to look around. No gaming skills needed.',
  },
  touch: {
    id: 'onboard-touch-start',
    icon: Compass,
    title: 'Start exploring',
    body: 'Slide your left thumb to walk, drag to look around, tap things to use them.',
  },
  follow: {
    id: 'onboard-mode-follow',
    icon: Footprints,
    title: 'Follow the guided tour',
    body: 'Sit back and automatically follow the presenter. Move at any time to take back control.',
  },
};

/**
 * Build the comfort-question cards for the boot screen. A click marks the
 * choice completed, fires analytics, highlights the card, and calls
 * `onChoice(choice)` exactly once (later clicks are ignored).
 * @param {boolean} touch - isTouchDevice() at build time
 * @param {(choice: string) => void} onChoice
 * @returns {HTMLElement}
 */
export function buildComfortCards(touch, onChoice) {
  const row = document.createElement('div');
  row.style.cssText =
    'display:flex; gap:14px; justify-content:center; flex-wrap:wrap; align-items:stretch;';
  let chosen = false;

  const keys = touch ? ['touch', 'follow'] : ['classic', 'hybrid', 'easy', 'follow'];
  for (const key of keys) {
    const def = CARDS[key];
    const card = document.createElement('button');
    card.type = 'button';
    card.id = def.id;
    card.style.cssText = `
      background:#fff; padding:16px 18px; border-radius:14px; cursor:pointer;
      display:flex; flex-direction:column; align-items:center; gap:10px;
      flex:1 1 0; min-width:150px; max-width:250px; text-align:center;
      border:2px solid #e5e7eb; font-family:inherit;
      transition:border-color 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;
    `;

    const title = document.createElement('div');
    // min-height reserves two lines so a title that wraps does not push its
    // card's icon and body down out of line with its neighbours.
    title.style.cssText = `font-weight:600; font-size:13px; color:${BRAND.primary}; text-transform:uppercase; letter-spacing:0.05em; display:inline-flex; align-items:center; justify-content:center; gap:6px; min-height:2.6em;`;
    title.append(createIcon(def.icon, { size: 15 }), def.title);

    const art = document.createElement('div');
    art.style.cssText = `display:flex; justify-content:center; color:${BRAND.primary};`;
    art.appendChild(createIcon(def.icon, { size: 36, strokeWidth: 1.5 }));

    const body = document.createElement('p');
    body.style.cssText = 'color:#6b7280; font-size:0.83rem; margin:0; line-height:1.45;';
    body.textContent = def.body;

    card.append(title, art, body);

    card.addEventListener('mouseenter', () => {
      if (!chosen) card.style.borderColor = BRAND.primary;
    });
    card.addEventListener('mouseleave', () => {
      if (card.getAttribute('aria-pressed') !== 'true') card.style.borderColor = '#e5e7eb';
    });
    card.addEventListener('click', () => {
      if (chosen) return;
      chosen = true;
      card.setAttribute('aria-pressed', 'true');
      card.style.borderColor = BRAND.primary;
      card.style.boxShadow = '0 6px 18px -6px rgba(153, 0, 230, 0.45)';
      // Dim the unchosen cards so the selection reads at a glance.
      for (const other of row.querySelectorAll('button')) {
        if (other !== card) other.style.opacity = '0.45';
      }
      markCompleted(key);
      try {
        analytics.track('onboarding_choice', { choice: key });
      } catch {
        /* ignore */
      }
      onChoice(key);
    });

    row.appendChild(card);
  }
  return row;
}

/**
 * Apply a choice's control-scheme / movement-mode side effects. Runs at
 * reveal time (arrivalSequence.js), when the scene and HUD exist.
 * @param {string} choice - classic | easy | hybrid | touch | follow
 */
export function applyOnboardingChoice(choice) {
  const touch = isTouchDevice();
  switch (choice) {
    case 'classic':
      setScheme('classic');
      setMovementMode('free');
      break;
    case 'hybrid':
      setScheme('hybrid');
      setMovementMode('free');
      break;
    case 'easy':
      setScheme('easy');
      setMovementMode('free');
      break;
    case 'touch':
      setMovementMode('free');
      break;
    case 'follow':
      // Guided tour implies the beginner-friendly scheme on desktop; the
      // persistent toggle lets the user flip modes for the rest of the
      // session. (Touch resolves its scheme from the device.)
      if (!touch) setScheme('easy');
      setMovementMode('follow');
      showMovementModeToggle();
      break;
    default:
      break;
  }
}

/**
 * Start the in-world coach marks for a first-run choice, with completion
 * analytics (onboarding_completed / onboarding_skipped).
 * @param {string} choice - classic | easy | hybrid | touch | follow
 */
export function beginCoachMarksFor(choice) {
  startCoachMarks(choice, {
    onComplete: (skipped) => {
      try {
        analytics.track(skipped ? 'onboarding_skipped' : 'onboarding_completed', {
          variant: choice,
        });
      } catch {
        /* ignore */
      }
    },
  });
}

/**
 * Replay the in-world tips for the current scheme (Help panel's
 * "Replay tour"). Skips the question - the scheme choice already exists.
 */
export function replayCoachMarks() {
  startCoachMarks(getScheme());
}
